import React from 'react'
import {Box, Flex} from '@chakra-ui/react'
import PropTypes from 'prop-types'

import {getGDIHideStyles} from '../../utils/gdi-styles'
import {
    getExpandedFromContentArea,
    getAllExpandedFromContentArea
} from '../../utils/gdi-content-area'
import {mapAspectRatio} from '../../utils/gdi-aspect-ratio'
import {buildResponsive, hasValue, mapResponsiveEnum} from '../../utils/gdi-responsive'
import GDILinkBlock from '../../assets/gdiLinkBlock'
import GDITooltip from '../../common/gdiTooltip'
import {renderComponentsSwitch} from '../../../utils/contentRenderer'
import {useHeaderContext} from '../../../../../contexts/header-context'
import {useIsHoverReveal, useGDIGridColumns} from '../../utils/gdi-link-context'

const VERTICAL_MAP = {
    Top: 'flex-start',
    Middle: 'center',
    Bottom: 'flex-end',
    'Space between': 'space-between'
}

const HORIZONTAL_MAP = {
    Left: 'flex-start',
    Center: 'center',
    Right: 'flex-end'
}

/* Grid equivalents for align-self / justify-self when the wrapper is `display: grid`
 * (overlay mode). Grid uses `start`/`end` rather than `flex-start`/`flex-end`. */
const GRID_VERTICAL_MAP = {
    Top: 'start',
    Middle: 'center',
    Bottom: 'end',
    'Space between': 'stretch'
}

const GRID_HORIZONTAL_MAP = {
    Left: 'start',
    Center: 'center',
    Right: 'end'
}

const TEXT_ALIGN_MAP = {
    Left: 'left',
    Center: 'center',
    Right: 'right'
}

/* Auto-margin pairs to position a sticky-column wrapper vertically inside its
 * parent — works in BOTH flex columns (where `align-self` is cross-axis only
 * and cannot move the child vertically) AND CSS grid cells. */
const STICKY_AUTO_MARGIN_MAP = {
    Top: {marginTop: 0, marginBottom: 'auto'},
    Middle: {marginTop: 'auto', marginBottom: 'auto'},
    Bottom: {marginTop: 'auto', marginBottom: 0},
    'Space between': {marginTop: 0, marginBottom: 0}
}

const buildStickyAutoMargin = (verticalRaw) => {
    const pick = (v) => STICKY_AUTO_MARGIN_MAP[v] || STICKY_AUTO_MARGIN_MAP.Top
    if (verticalRaw && typeof verticalRaw === 'object' && !Array.isArray(verticalRaw)) {
        const marginTop = {}
        const marginBottom = {}
        Object.entries(verticalRaw).forEach(([bp, v]) => {
            const m = pick(v)
            marginTop[bp] = m.marginTop
            marginBottom[bp] = m.marginBottom
        })
        return {marginTop, marginBottom}
    }
    const m = pick(verticalRaw)
    return {marginTop: m.marginTop, marginBottom: m.marginBottom}
}

const POSITION_MAP = {
    'Content overlays image': 'absolute',
    'Content above image': 'static',
    'Content below image': 'static'
}

const ORDER_MAP = {
    'Content above image': -1,
    'Content overlays image': 0,
    'Content below image': 0
}

const DIRECTION_MAP = {
    Stacked: 'column',
    'Side by Side': 'row'
}

const isAnyTrue = (v) =>
    v === true ||
    (typeof v === 'object' && v !== null && Object.values(v).some(Boolean))

/**
 * Group consecutive CTA blocks into a single render group, leaving other blocks
 * standalone. CTAs grouping (Side by Side vs Stacked) per the Content Tile spec
 * only applies "when more than one CTA is added in sequence in the layout"
 * — non-CTA children render in normal vertical flow.
 *
 * @param {Array} blocks - Slot children, each with a `__typename`
 * @returns {Array<{kind: 'cta', items: Array} | {kind: 'item', item: Object}>}
 */
const groupConsecutiveCtas = (blocks) => {
    const out = []
    let run = null
    for (const block of blocks) {
        if (block?.__typename === 'GDICtaBlock') {
            if (!run) {
                run = {kind: 'cta', items: []}
                out.push(run)
            }
            run.items.push(block)
        } else {
            run = null
            out.push({kind: 'item', item: block})
        }
    }
    return out
}

const getCtaGroupWidth = (items) => {
    const width = {}

    items.forEach((item) => {
        const minWidth = buildResponsive(
            item?.GDICtaBlockStylesPerViewType,
            (entry) => entry.MinWidth,
            undefined
        )

        const applyWidth = (bp, value) => {
            if (!hasValue(value)) return
            const normalized = typeof value === 'string' ? value.trim() : value
            if (typeof normalized === 'string' && normalized.endsWith('%')) {
                width[bp] = normalized
            }
        }

        if (minWidth && typeof minWidth === 'object' && !Array.isArray(minWidth)) {
            Object.entries(minWidth).forEach(([bp, value]) => applyWidth(bp, value))
            return
        }

        applyWidth('base', minWidth)
    })

    return Object.keys(width).length > 0 ? width : undefined
}

const GDIContentTileBlock = ({
    HideBlockOn,
    HideOnDevices,
    MediaImageVideo,
    ContentSlot,
    GDIContentTileBlockLink,
    GDIContentTileBlockStylesPerViewType,
    TooltipContent,
    /* Set by ProductGrid when this tile renders as a PLP grid interruptor.
     * Forces the tile to ignore its authored ContentAspectRatio so its height
     * is driven by the SimpleGrid row (i.e. equal to the product tile's image
     * height) instead of imposing e.g. 19/28 on top of it. */
    isGridInterruptor = false
}) => {
    const hideStyles = getGDIHideStyles(HideBlockOn || HideOnDevices)
    if (hideStyles.doNotLoadComponent) return null

    /* The site header is fixed at the top of the viewport, so sticky content needs an
     * offset equal to the header's measured height (kept in sync via the header context
     * + ResizeObserver). Falls back to 0 on SSR / before measurement. */
    const {headerHeight} = useHeaderContext()

    /* When inside a parent block authored with `DisplayTextAndCTAsOnHover` (e.g. the
     * carousel/grid), the content + gradient layers fade in only on hover. Touch
     * devices (no `hover: hover`) keep them visible always. */
    const isHoverReveal = useIsHoverReveal()

    /* Parent grid's per-breakpoint column count (from GDICarouselGridBlock). Used to
     * clamp this tile's authored ColSpan so it never exceeds the available tracks
     * — otherwise CSS Grid creates zero-width implicit columns and the layout breaks
     * (e.g. ColSpan=4 inside a 2-column mobile grid). */
    const parentGridColumns = useGDIGridColumns()

    const mediaBlock = getExpandedFromContentArea(MediaImageVideo)
    const slotBlocks = getAllExpandedFromContentArea(ContentSlot)
    const linkBlock = getExpandedFromContentArea(GDIContentTileBlockLink)
    const hasTooltip = getAllExpandedFromContentArea(TooltipContent).length > 0

    const stylesArr = GDIContentTileBlockStylesPerViewType

    /* Raw responsive enums — read once, then map into Chakra values via mapResponsiveEnum.
     * NOTE: CMS field names are `MediaContentDisplayMode` and `CTAsGroupingDisplay`. */
    const displayMode = buildResponsive(
        stylesArr,
        (e) => e.MediaContentDisplayMode,
        'Content overlays image'
    )
    const authoredOverlayMode = buildResponsive(
        stylesArr,
        (e) =>
            hasValue(e.MediaContentDisplayMode)
                ? e.MediaContentDisplayMode === 'Content overlays image'
                : null,
        false
    )
    const ctasGrouping = buildResponsive(stylesArr, (e) => e.CTAsGroupingDisplay, 'Side by Side')
    const verticalRaw = buildResponsive(stylesArr, (e) => e.ContentVerticalPosition, 'Middle')
    const horizontalRaw = buildResponsive(stylesArr, (e) => e.ContentHorizontalPosition, 'Center')

    /* Other responsive props passed through verbatim. */
    const stickyContent = buildResponsive(stylesArr, (e) => e.StickyContent === 'Yes', false)
    /* StickyColumn pins the entire tile (column) inside the parent grid container,
     * so a shorter column stays in view while the taller sibling scrolls past. */
    const stickyColumn = buildResponsive(stylesArr, (e) => e.StickyColumn === 'Yes', false)
    const transparentGradient = buildResponsive(stylesArr, (e) => e.TransparentGradientCss, null)
    const backgroundColor = buildResponsive(stylesArr, (e) => e.BackgroundColor, 'transparent')
    const aspectRatioRaw = buildResponsive(stylesArr, (e) => e.ContentAspectRatio, null)
    const aspectRatio = mapAspectRatio(aspectRatioRaw)
    const contentMaxWidth = buildResponsive(stylesArr, (e) => e.ContentMaxWidth, null)
    const paddings = buildResponsive(stylesArr, (e) => e.Paddings, undefined)
    const margins = buildResponsive(stylesArr, (e) => e.Margins, undefined)
    const colSpan = buildResponsive(stylesArr, (e) => Number(e.ColSpan) || null, null)

    const justify = mapResponsiveEnum(verticalRaw, VERTICAL_MAP)
    const alignItems = mapResponsiveEnum(horizontalRaw, HORIZONTAL_MAP)
    const textAlign = mapResponsiveEnum(horizontalRaw, TEXT_ALIGN_MAP)
    const contentPosition = mapResponsiveEnum(displayMode, POSITION_MAP)
    const contentOrder = mapResponsiveEnum(displayMode, ORDER_MAP)
    const contentDirection = mapResponsiveEnum(ctasGrouping, DIRECTION_MAP)
    /* Build clamped grid-column. For every breakpoint where the tile authors a ColSpan,
     * cap it to the parent's column count for that breakpoint (so e.g. ColSpan=4 inside
     * a 2-col mobile grid renders as `1 / -1` — full row — rather than overflowing). */
    const gridColumn = (() => {
        if (colSpan === null || colSpan === undefined) return undefined
        const colsAt = (bp) => {
            if (parentGridColumns && typeof parentGridColumns === 'object') {
                return Number(parentGridColumns[bp]) || null
            }
            return Number(parentGridColumns) || null
        }
        const buildSpan = (n, bp) => {
            const max = colsAt(bp)
            if (max && Number(n) >= max) return '1 / -1'
            return `span ${n}`
        }
        if (typeof colSpan === 'object') {
            return Object.fromEntries(
                Object.entries(colSpan).map(([bp, n]) => [bp, buildSpan(n, bp)])
            )
        }
        // scalar — assume base breakpoint
        return buildSpan(colSpan, 'base')
    })()
    const gridAlignSelf = mapResponsiveEnum(verticalRaw, GRID_VERTICAL_MAP)
    const gridJustifySelf = mapResponsiveEnum(horizontalRaw, GRID_HORIZONTAL_MAP)

    /* Build the gradient overlay element only when authored. Sits above the media but
     * below the content layer so text remains legible over busy imagery. */
    const gradientOverlay = transparentGradient ? (
        <Box
            data-gdi-overlay="gradient"
            position="absolute"
            inset={0}
            pointerEvents="none"
            background={transparentGradient}
            zIndex={1}
        />
    ) : null

    /* The Flex's `position` prop drives whether the content layer overlays the media
     * (absolute) or sits in flow (static). When absolute, it must fill the parent and
     * get inner padding so text isn't flush against the edges. */
    const hasOverlayPosition =
        contentPosition === 'absolute' ||
        (typeof contentPosition === 'object' &&
            Object.values(contentPosition).includes('absolute'))
    const isOverlayMode = Boolean(mediaBlock) && hasOverlayPosition
    const hasAuthoredOverlayMode = isAnyTrue(authoredOverlayMode)
    /* Whether the tile EXPLICITLY authored a horizontal position (i.e. not left
     * as empty / "Default / Inherit"). We only let the tile override its child
     * text blocks' own `TextAlignment` when this is true — otherwise the text
     * blocks keep their authored alignment, which is the long-standing default. */
    const hasAuthoredHorizontalPosition = isAnyTrue(
        buildResponsive(
            stylesArr,
            (e) => (hasValue(e.ContentHorizontalPosition) ? true : null),
            false
        )
    )
    /* Text blocks normally stretch to 100% so their own `TextAlignment` resolves
     * against the full available width. Inside a Content Tile that's harmful in
     * two scenarios because it lets each child text block override the tile's
     * authored `ContentHorizontalPosition`:
     *   - overlay mode (content is a sized grid item placed by justifySelf)
     *   - text-only tile (no media) WITH an explicitly authored horizontal
     *     position: the tile's Flex owns horizontal placement via
     *     alignItems/textAlign; sibling CTAs honour that, so text blocks must
     *     too — otherwise text sticks to one edge while the CTAs sit
     *     centred (or vice versa). When the author left the position as
     *     Default / Inherit, we leave the text blocks alone so their own
     *     `TextAlignment` keeps working. */
    const disableTextAlignmentLayoutStretch =
        !mediaBlock && hasAuthoredHorizontalPosition
            ? true
            : hasAuthoredOverlayMode
            ? authoredOverlayMode
            : isOverlayMode
    const inFlowContentPosition =
        contentPosition && typeof contentPosition === 'object'
            ? Object.fromEntries(
                  Object.entries(contentPosition).map(([bp, value]) => [
                      bp,
                      value === 'absolute' ? 'static' : value
                  ])
              )
            : contentPosition === 'absolute'
            ? 'static'
            : contentPosition

    const isStickyOn = isAnyTrue(stickyContent)
    /* In no-media mode the wrapper is sized by its content, so the content's own
     * `position: sticky` has no scroll range to pin against. Promote
     * `StickyContent: Yes` to wrapper-level sticky — same effect as
     * `StickyColumn: Yes` — so the tile pins within its parent column while
     * sibling columns scroll past, keeping its authored in-flow position. */
    const isStickyColumn = isAnyTrue(stickyColumn) || (!mediaBlock && isStickyOn)

    /* In overlay mode we stack media and content via CSS Grid (single cell). The media
     * stretches to give the tile its height, while the content sizes to its own content
     * and uses align-self/justify-self for placement. This is what enables `position:
     * sticky` on the content to pin within the tile (green box) while the page scrolls. */

    /* `getGDIHideStyles` returns a per-breakpoint `display` object like
     * `{base: 'block', md: 'none', lg: 'block'}`. The wrapper needs
     * `display: flex` (above/below modes) or `display: grid` (overlay mode)
     * so children can be ordered or stacked. Hidden breakpoints stay 'none'.
     *
     * We emit raw `@media` rules into sx (not a Chakra responsive `display`
     * prop) so the hide rules sit in the same cascade layer as the other
     * authored sx rules — otherwise the Chakra responsive shorthand loses
     * to raw `@media` selectors already present in sx and the block stays
     * visible on hidden breakpoints. */
    const visibleDisplay = isOverlayMode ? 'grid' : 'flex'
    const hideDisplay = hideStyles.display
    const wrapperDisplaySx = hideDisplay
        ? {
              display: hideDisplay.base === 'none' ? 'none' : visibleDisplay,
              '@media screen and (min-width: 48em)': {
                  display: hideDisplay.md === 'none' ? 'none' : visibleDisplay
              },
              '@media screen and (min-width: 62em)': {
                  display: hideDisplay.lg === 'none' ? 'none' : visibleDisplay
              }
          }
        : {display: visibleDisplay}

    const hasAspectRatio =
        !isGridInterruptor &&
        (typeof aspectRatio === 'string' ||
            (aspectRatio &&
                typeof aspectRatio === 'object' &&
                Object.keys(aspectRatio).length > 0))

    /* When any CTA group inside the tile authors a `MinWidth: 100%` (typically
     * a stacked, full-width button), the wrapper CTA Flex gets `width: 100%`
     * (see render below). In overlay mode the content Flex is `fit-content` so
     * `justifySelf` can position it — but that makes the CTA's `%` resolve
     * against a button-sized parent and collapse to text width. To honour the
     * authored full-width CTA we widen the content Flex to 100% only at the
     * breakpoints where a CTA group requests 100% width; other breakpoints
     * keep `fit-content` and continue to be placed by `justifySelf`. */
    const ctaGroupFullWidthBps = (() => {
        if (!isOverlayMode) return null
        const bps = new Set()
        const groups = groupConsecutiveCtas(slotBlocks)
        groups.forEach((group) => {
            if (group.kind !== 'cta') return
            const w = getCtaGroupWidth(group.items)
            if (!w) return
            Object.entries(w).forEach(([bp, value]) => {
                if (value === '100%') bps.add(bp)
            })
        })
        return bps.size > 0 ? bps : null
    })()

    const overlayContentWidth = ctaGroupFullWidthBps
        ? {
              base: ctaGroupFullWidthBps.has('base') ? '100%' : 'fit-content',
              md: ctaGroupFullWidthBps.has('md') ? '100%' : 'fit-content',
              lg: ctaGroupFullWidthBps.has('lg') ? '100%' : 'fit-content'
          }
        : 'fit-content'

    const tile = (
        <Box
            data-block-type="GDIContentTileBlock"
            data-sticky-column={isStickyColumn ? 'true' : undefined}
            flexDirection="column"
            position={isStickyColumn ? 'sticky' : 'relative'}
            top={isStickyColumn ? `${headerHeight || 0}px` : undefined}
            /* Pair `top` with `bottom: 0` so the wrapper pins on BOTH scroll
             * directions inside the parent column — mirrors the with-media
             * content sticky setup. Without `bottom`, scrolling back up past
             * the pin point releases the wrapper and it no longer sticks. */
            bottom={isStickyColumn ? 0 : undefined}
            /* No explicit height: grid items default to `align-self: stretch`, so the
             * wrapper naturally fills the row when needed (giving sticky content scroll
             * room, and stretching text-only tiles next to image tiles). Setting
             * `height: 100%` here would conflict with `margin` (the margin would push
             * the box outside the cell), so we rely on grid stretch instead. */
            backgroundColor={backgroundColor}
            margin={margins}
            gridColumn={gridColumn}
            /* As a PLP grid interruptor we want the tile to stretch to the
             * SimpleGrid row (= product tile image height). The outer Box from
             * ProductGrid is the grid item and naturally stretches, but this
             * inner wrapper has no intrinsic height, so we have to fill it
             * explicitly. */
            height={isGridInterruptor ? '100%' : undefined}
            width={isGridInterruptor ? '100%' : undefined}
            sx={{
                ...wrapperDisplaySx,
                /* The carousel/grid parent applies `flex: 1` to children via a
                 * `& > *:not(...)` selector (specificity 0,2,1), which stretches
                 * the tile to the row height and overrides any plain `flex: ...`
                 * we set here (specificity 0,1,0). When an aspect ratio is
                 * authored we instead want the tile sized by `width × ratio`,
                 * so we use a self-targeting attribute selector to match the
                 * parent's specificity and reliably opt out of flex-grow. CSS
                 * aspect-ratio then computes height from the explicit width.
                 *
                 * Only applied at the wrapper level in overlay mode — in above/below
                 * modes the aspect ratio is applied to the media element only so the
                 * text can flow underneath/above without being clipped by `overflow:
                 * hidden`. */
                /* Apply the wrapper aspect ratio in two cases:
                 *  - overlay mode (single grid cell, content stacks on
                 *    media; the media stretches to fill the ratio)
                 *  - non-overlay mode WITHOUT media (text-only tile; the
                 *    media element that would normally carry the ratio
                 *    isn't rendered, so we apply it to the wrapper so the
                 *    text is laid out inside the authored ratio)
                 *
                 * Skip when non-overlay AND media is present — the media
                 * box already carries the ratio and the text can flow
                 * under/over without being clipped by `overflow: hidden`. */
                ...(hasAspectRatio && (isOverlayMode || !mediaBlock)
                    ? {
                          aspectRatio,
                          '&[data-block-type="GDIContentTileBlock"]': {
                              flex: 'none',
                              alignSelf: 'flex-start'
                          }
                      }
                    : {}),
                ...(isOverlayMode
                    ? {gridTemplateAreas: '"stack"', gridTemplateColumns: '1fr'}
                    : {}),
                /* Self-targeted selector raises specificity above the parent
                 * carousel/grid wrapper's `& > *` rule (which forces flex: 1 to
                 * stretch tiles to the row's height). For sticky-column to work,
                 * the tile must keep its natural height — otherwise it fills the
                 * containing block and has no room to pin.
                 *
                 * Vertical placement uses auto-margins from the authored
                 * `ContentVerticalPosition` (Top / Middle / Bottom). `align-self`
                 * cannot be used here because the parent slide wrapper is a flex
                 * column (see gdiCarouselGridBlock), where `align-self` controls
                 * the cross axis (horizontal). Auto-margins work in both flex
                 * columns and grid cells, so the pinned tile starts at the
                 * placed position before sticky engages. */
                ...(isStickyColumn
                    ? {
                          '&[data-sticky-column="true"]': {
                              flex: '0 0 auto',
                              height: 'auto',
                              width: '100%',
                              ...buildStickyAutoMargin(verticalRaw)
                          }
                      }
                    : {}),
                ...(hasAspectRatio && isOverlayMode ? {overflow: 'hidden'} : {}),
                ...(isHoverReveal
                    ? {
                          '@media (hover: hover) and (min-width: 62em)': {
                              '[data-gdi-content-layer="content"], [data-gdi-overlay="gradient"]': {
                                  opacity: 0,
                                  transition: 'opacity 0.25s ease'
                              },
                              '&:hover [data-gdi-content-layer="content"], &:hover [data-gdi-overlay="gradient"]': {
                                  opacity: 1
                              }
                          }
                      }
                    : {})
            }}
        >
            {/* Media layer. In overlay mode it occupies the single grid cell and lets the
             * image define the height. In above/below modes it's a flex item ordered
             * relative to the content. The gradient sits inside, absolutely positioned
             * to cover the image. */}
            {mediaBlock && (
                <Box
                    position="relative"
                    width="100%"
                    order={0}
                    sx={{
                        ...(isOverlayMode ? {gridArea: 'stack'} : {}),
                        /* In above/below modes the aspect ratio belongs on the media
                         * box, not the tile wrapper — otherwise the text content
                         * (rendered as a sibling) gets clipped by the wrapper's
                         * `overflow: hidden`. The inner image/video is forced to
                         * fill the box with `object-fit: cover` + `object-position:
                         * center` so it's cropped around its middle when its
                         * natural ratio differs from the authored container ratio
                         * (e.g. a tall image inside a 1/1 box). */
                        ...(hasAspectRatio && !isOverlayMode
                            ? {
                                  aspectRatio,
                                  overflow: 'hidden',
                                  '& img, & video, & picture': {
                                      width: '100%',
                                      height: '100%',
                                      objectFit: 'cover',
                                      objectPosition: 'center',
                                      display: 'block'
                                  },
                                  /* GDIImageBlock wraps the picture in a Box that
                                   * defaults to maxWidth 100% — make sure that wrapper
                                   * also fills the aspect-ratio box vertically so the
                                   * image inside has a height to cover. */
                                  '& > [data-block-type="GDIImageBlock"], & > [data-block-type="GDIVideoBlock"]': {
                                      width: '100%',
                                      height: '100%'
                                  }
                              }
                            : {})
                    }}
                >
                    {renderComponentsSwitch(mediaBlock)}
                    {gradientOverlay}
                </Box>
            )}


            {/* Content layer — outer Flex stacks children vertically and handles overall
             * alignment. CTA grouping (Side by Side vs Stacked) ONLY applies to runs of
             * consecutive CTA blocks; non-CTA children render in normal flow.
             *
             * Overlay mode uses grid-cell stacking with align/justify-self instead of
             * absolute positioning so `position: sticky` (when authored) can pin the
             * content to the top of the viewport while the tile scrolls past. */}
            {slotBlocks.length > 0 && (
                <Flex
                    data-gdi-content-layer="content"
                    position={
                        isOverlayMode
                            ? isStickyOn
                                ? 'sticky'
                                : 'relative'
                            : inFlowContentPosition
                    }
                    top={isStickyOn ? `${headerHeight || 0}px` : undefined}
                    bottom={isStickyOn ? 0 : undefined}
                    order={contentOrder}
                    direction="column"
                    justifyContent={justify}
                    alignItems={alignItems}
                    textAlign={textAlign}
                    /* In overlay mode the content layer is a CSS Grid item
                     * positioned via `justifySelf` (horizontal) and
                     * `alignSelf` (vertical). For those to actually move the
                     * box, it must NOT span the full track — otherwise
                     * `Right`/`Center` look identical to `Left`. So we shrink
                     * to content width here (still bounded by an authored
                     * `ContentMaxWidth` via `maxWidth` below) and let
                     * `justifySelf` place it. In above/below mode the
                     * content is in normal flow under/over the media and
                     * needs the full row width so `textAlign` /
                     * `alignItems` resolve over the visible area. */
                    width={isOverlayMode ? overlayContentWidth : '100%'}
                    maxHeight={hasAspectRatio ? '100%' : undefined}
                    overflow={hasAspectRatio ? 'hidden' : undefined}
                    /* Content height: when sticky we MUST keep natural height so the
                     * sticky element is shorter than its container and can pin while
                     * the wrapper scrolls past. In overlay mode the wrapper is a CSS
                     * Grid cell that stretches to fill the row by default — let the
                     * content size to its own contents so `alignSelf` (vertical
                     * position) can move it. Only stretch (height: 100%) in
                     * non-overlay mode without media only when an aspect ratio gives
                     * the wrapper a real height; otherwise 100% can resolve to 0 in
                     * text-only CLP content and collapse the tile. */
                    height={
                        !mediaBlock && !isStickyOn && !isOverlayMode && hasAspectRatio
                            ? '100%'
                            : 'auto'
                    }
                    /* In non-overlay, no-media mode the content Flex is the wrapper's
                     * only flow child. Without `flex: 1` it sits at its natural
                     * height at the top of a stretched wrapper (e.g. PLP grid
                     * interruptor row, or any parent giving the tile a height),
                     * which makes `justifyContent` (ContentVerticalPosition) a
                     * no-op. Using `flex: 1` instead of `height: 100%` keeps the
                     * CLP text-only safety (no defined wrapper height → nothing to
                     * distribute → no collapse), while letting the content fill any
                     * extra space so vertical placement actually moves children.
                     * When sticky-column is on (incl. the no-media promotion), the
                     * wrapper is short and pins as a whole — `flex: 1` would make
                     * it tall again and break the pin, so we skip it. */
                    flex={
                        !mediaBlock && !isOverlayMode && !isStickyColumn ? 1 : undefined
                    }
                    maxWidth={contentMaxWidth || undefined}
                    padding={paddings}
                    zIndex={2}
                    sx={
                        isOverlayMode
                            ? {
                                  gridArea: 'stack',
                                  /* Always honour authored vertical/horizontal
                                   * placement. Sticky needs a non-stretch
                                   * align so the box can be shorter than its
                                   * container; default to `start` if the
                                   * author left vertical position unset. */
                                  alignSelf: isStickyOn
                                      ? gridAlignSelf || 'start'
                                      : gridAlignSelf,
                                  justifySelf: gridJustifySelf
                              }
                            : undefined
                    }
                >
                    {groupConsecutiveCtas(slotBlocks).map((group, gIdx) => {
                        if (group.kind === 'cta') {
                            /* Apply an authored CTA MinWidth (%) to the group wrapper so
                             * the CTA actually stretches. Also runs in overlay mode now
                             * — the content Flex's `width` (set above) is widened to
                             * 100% at the matching breakpoints so the % resolves
                             * against the tile rather than against a `fit-content`
                             * parent (which would collapse the CTA to its text). */
                            const ctaGroupWidth = getCtaGroupWidth(group.items)

                            return (
                                <Flex
                                    key={`gdi-ct-cta-${gIdx}`}
                                    data-gdi-cta-group="true"
                                    direction={contentDirection}
                                    flexWrap="wrap"
                                    gap="8px"
                                    width={ctaGroupWidth}
                                >
                                    {group.items.map((child, idx) => (
                                        <React.Fragment
                                            key={child?.ContentLink?.GuidValue || `gdi-ct-cta-${gIdx}-${idx}`}
                                        >
                                            {renderComponentsSwitch(child)}
                                        </React.Fragment>
                                    ))}
                                </Flex>
                            )
                        }
                        return (
                            <React.Fragment
                                key={group.item?.ContentLink?.GuidValue || `gdi-ct-${gIdx}`}
                            >
                                {renderComponentsSwitch(
                                    group.item,
                                    group.item?.__typename === 'GDITextBlock'
                                        ? {disableTextAlignmentLayoutStretch}
                                        : undefined
                                )}
                            </React.Fragment>
                        )
                    })}
                </Flex>
            )}

            {/* Tooltip control — sits above the content layer (zIndex: 3) in
             * the top-left of the tile. The trigger handler stops propagation
             * so clicks on the tooltip don't follow the (optional)
             * GDILinkBlock wrapping this tile. Renders nothing when the
             * author hasn't provided any TooltipContent. */}
             {hasTooltip && (
                <Box
                    data-gdi-tile-tooltip="true"
                    position="absolute"
                    top={{base: '16px', lg: '24px'}}
                    left={{base: '16px', lg: '24px'}}
                    zIndex={3}
                >
                    <GDITooltip tooltipContent={TooltipContent} placement="right" />
                </Box>
            )}
        </Box>
    )

    if (linkBlock) {
        return <GDILinkBlock {...linkBlock}>{tile}</GDILinkBlock>
    }

    return tile
}

GDIContentTileBlock.propTypes = {
    HideBlockOn: PropTypes.string,
    HideOnDevices: PropTypes.string,
    MediaImageVideo: PropTypes.array,
    ContentSlot: PropTypes.array,
    GDIContentTileBlockLink: PropTypes.array,
    GDIContentTileBlockStylesPerViewType: PropTypes.array,
    TooltipContent: PropTypes.array,
    isGridInterruptor: PropTypes.bool
}

export default GDIContentTileBlock
